import React, { useRef } from 'react';

import { useDispatch } from 'react-redux';

import { ClickToCallAllInternationalDialingPhoneCodesOutputDto, OperationType } from '@api/ecommerce-xp';
import { CoreHelper } from '@bcp-channels-sharedcomponents-reactjs/core/dist/$helpers';
import { useDidMount, useTranslation } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { useForm } from '@bcp-channels-sharedcomponents-reactjs/form-utils/dist/$components';
import { PopupMenuItem, PopupMenuRef } from '@bcp-nextgen-dx-component-factory/design-system';
import { ActiveItem } from '@bcp-nextgen-dx-component-factory/design-system';
import { useFetchClickToCallAllInternationalDialingPhoneCodes } from '@services';
import { clearIntegratorFormInput, saveUserInput, useIntegrators, useServiceResponse, useUserInput } from '@store';

interface IntegratorDataHelperResources {
	title: string;
	corporateIntegrator: string;
	corporate: string;
	hasNotTechnicIntegrator: string;
	link: string;
}

interface IntegratorDataHelperOutput {
	resources: IntegratorDataHelperResources;
	buildIntegrators: PopupMenuItem[];
	selectedOneIntegrator: boolean;
	clickToCallAllInternationalDialingPhoneCodes: ClickToCallAllInternationalDialingPhoneCodesOutputDto | undefined;
	popupMenuIntegratorRef: React.RefObject<PopupMenuRef>;
	handlePressSelectIntegrator: (item: ActiveItem | undefined) => void;
}

export const useIntegratorDataHelper = (): IntegratorDataHelperOutput => {
	const t = useTranslation();
	const dispatch = useDispatch();
	const { selectedIntegrator, forms } = useUserInput();
	const { fetch: retrieveCountries } = useFetchClickToCallAllInternationalDialingPhoneCodes();
	const { clickToCallAllInternationalDialingPhoneCodes } = useServiceResponse();
	const integrators = useIntegrators();
	const popupMenuIntegratorRef = React.useRef<PopupMenuRef>(null);
	const form = useForm('integratorData');
	const popupMenuCountryRef = useRef<PopupMenuRef>(null);

	const buildIntegrators = React.useMemo(() => {
		const result: PopupMenuItem[] = integrators.map((integrator) => {
			return {
				content: `${integrator.name}`,
			};
		});
		return result;
	}, [integrators]);

	const selectedIntegratorIndex = buildIntegrators.findIndex((x) => x.content === selectedIntegrator?.name);

	useDidMount(() => {
		if (clickToCallAllInternationalDialingPhoneCodes === undefined) {
			retrieveCountries({ operationType: OperationType.CODES_MB_WAY });
		}

		if (!forms?.integratorData?.info?.email) {
			dispatch(saveUserInput({ selectedIntegrator: undefined }));
		} else {
			popupMenuIntegratorRef.current?.setActiveItem(selectedIntegratorIndex);
		}
	});

	const resources: IntegratorDataHelperResources = {
		title: t('placeholders.integratorData.title'),
		corporateIntegrator: t('placeholders.integratorData.corporateIntegrator'),
		corporate: t('general.input.corporate'),
		hasNotTechnicIntegrator: t('placeholders.integratorData.hasNotTechnicIntegrator'),
		link: t('placeholders.integratorData.hasNotTechnicIntegrator.link'),
	};

	const selectedOneIntegrator = selectedIntegrator?.code !== undefined;

	const handlePressSelectIntegrator = (item: ActiveItem | undefined) => {
		if (item?.id !== selectedIntegratorIndex) {
			form.clear({});
			dispatch(clearIntegratorFormInput());
		}

		if (item?.content !== undefined) {
			const selectedIdIntegrator = integrators.filter(
				(integrator) => integrator.name.toLocaleLowerCase() === item?.content.toLocaleLowerCase()
			)[0];
			if (!CoreHelper.isNullOrEmpty(selectedIdIntegrator)) {
				dispatch(saveUserInput({ selectedIntegrator: selectedIdIntegrator }));
				popupMenuCountryRef.current?.setActiveItem(0);
			}
		} else {
			dispatch(saveUserInput({ selectedIntegrator: undefined }));
		}
	};

	return {
		resources,
		buildIntegrators,
		selectedOneIntegrator,
		clickToCallAllInternationalDialingPhoneCodes,
		popupMenuIntegratorRef,
		handlePressSelectIntegrator,
	};
};
